import { resolveMemberMediaUrl } from '@formosa/shared/utils/mediaUrl';
import {
  coerceDateTimeLocal,
  snowflakeId,
  toApiDateTime,
} from '../journey-accommodation/accommodationModel';
import { coerceDateTime, coerceLocalDate } from '../my-journeys/myJourneysModel';

/** Backend enums — expose only product-safe types in FE. */
export const MEMORY_TYPES = Object.freeze(['JOURNAL', 'PRIVATE_NOTE']);
export const MEMORY_VISIBILITIES = Object.freeze(['PRIVATE', 'PUBLIC']);
export const MEMORY_STATUSES = Object.freeze(['ACTIVE', 'HIDDEN', 'DELETED']);

export const MEMORY_TYPE_LABELS = Object.freeze({
  JOURNAL: '日記',
  PRIVATE_NOTE: '私人筆記',
  MOOD: '心情',
  PHOTO_NOTE: '照片筆記',
  PLACE_NOTE: '地點筆記',
  FOOD_NOTE: '美食筆記',
  HIGHLIGHT: '亮點',
});

export const MEMORY_VISIBILITY_LABELS = Object.freeze({
  PRIVATE: '私人',
  PUBLIC: '公開',
  FOLLOWERS: '追蹤者',
});

export const MEMORY_STATUS_LABELS = Object.freeze({
  ACTIVE: '可見',
  HIDDEN: '已隱藏',
  DELETED: '已刪除',
});

export const WRITABLE_MEMORY_LIFECYCLES = Object.freeze(['READY', 'TRAVELING', 'COMPLETED']);

/** Backend domain MAX_MEDIA_COUNT = 20. */
export const MEMORY_MEDIA_BACKEND_MAX = 20;
/** Presentation UX bound — not a backend invariant. */
export const MEMORY_MEDIA_UI_LIMIT = 6;
export const MEMORY_MEDIA_MAX_BYTES = 5 * 1024 * 1024;
export const MEMORY_MEDIA_ALLOWED_EXTENSIONS = Object.freeze(['.jpg', '.jpeg', '.png', '.gif']);
export const MEMORY_MEDIA_ALLOWED_MIME = Object.freeze([
  'image/jpeg',
  'image/png',
  'image/gif',
]);

export const MEDIA_UPLOAD_STATUS = Object.freeze({
  LOCAL_SELECTED: 'LOCAL_SELECTED',
  UPLOADING: 'UPLOADING',
  UPLOADED: 'UPLOADED',
  FAILED: 'FAILED',
});

export function memoryTypeLabel(type) {
  return MEMORY_TYPE_LABELS[String(type || '')] || String(type || '—');
}

export function memoryVisibilityLabel(visibility) {
  return MEMORY_VISIBILITY_LABELS[String(visibility || '')] || String(visibility || '—');
}

export function memoryStatusLabel(status) {
  return MEMORY_STATUS_LABELS[String(status || '')] || String(status || '—');
}

export function memoryVisibilityHint(visibility) {
  if (visibility === 'PRIVATE') return '只有自己可見';
  if (visibility === 'PUBLIC') {
    return '公開：可能出現在公開旅程內容中（若旅程已公開）。';
  }
  return '';
}

/** Honest copy when PRIVATE Memory also has media (public media proxy gap). */
export function privateMemoryMediaAccessNote() {
  return '日記內容為私人；圖片檔案目前使用平台既有媒體存取機制。';
}

export function classifyMemoryError(cause) {
  const code = cause?.code ?? cause?.raw?.code;
  const key = String(cause?.raw?.key || cause?.message || '');
  if (code === 90003 || code === 90005 || code === 90006
      || key.includes('token.not.found') || key.includes('token.illegal') || key.includes('token.expired')) {
    return 'unauthorized';
  }
  if (code === 92001 || key === 'travel.journey.not.found') return 'journey-not-found';
  if (key === 'travel.memory.not.found' || (code === 95102 && key.includes('memory'))) return 'not-found';
  if (key.includes('permission.denied') || code === 95101) return 'forbidden';
  if (key.includes('concurrent.modification')) return 'conflict';
  if (key.includes('idempotency.conflict')) return 'idempotency-conflict';
  if (key.includes('lifecycle.conflict')) return 'lifecycle';
  if (code === 95104 || key === 'travel.memory.im.unavailable') return 'unavailable';
  if (code === 95100 || key.includes('validation')) return 'validation';
  if (code === 95102) return 'not-found';
  if (code === 95103) return 'conflict';
  return 'error';
}

export function createMemoryClientRequestId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `mem-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
}

export function createLocalMediaId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `media-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
}

export function canWriteMemory({ role, lifecycleStatus }) {
  if (!WRITABLE_MEMORY_LIFECYCLES.includes(lifecycleStatus)) return false;
  return role === 'OWNER' || role === 'COORDINATOR' || role === 'MEMBER';
}

export function isMemoryAuthor(entry, viewerMemberId) {
  if (entry == null || viewerMemberId == null || viewerMemberId === '') return false;
  return String(entry.authorMemberId) === String(viewerMemberId);
}

export function contentPreview(content, max = 120) {
  const text = String(content || '').trim().replace(/\s+/g, ' ');
  if (!text) return '';
  if (text.length <= max) return text;
  return `${text.slice(0, max)}…`;
}

/** 媒體呈現分類：只有 IMAGE 會解析圖片網址；VIDEO 與無法辨識的媒體只顯示文字佔位。 */
export const MEMORY_MEDIA_KIND = Object.freeze({
  IMAGE: 'IMAGE',
  VIDEO: 'VIDEO',
  UNSUPPORTED: 'UNSUPPORTED',
});

/** 與後端 MemoryMediaObjectKeyRules 的圖片 key 規則一致。 */
const MEMORY_IMAGE_OBJECT_KEY = /^(?:image\/member\/|travel\/image\/[0-9]{8}\/)[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}\.(?:jpg|jpeg|png|gif)$/;
/** 與後端私有影片 key 規則一致；private key 不是播放網址，不可送公共代理。 */
const MEMORY_VIDEO_OBJECT_KEY = /^private\/travel\/video\/[0-9]{8}\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\.mp4$/;

export function isMemoryImageObjectKey(objectKey) {
  return MEMORY_IMAGE_OBJECT_KEY.test(String(objectKey || '').trim());
}

export function isMemoryVideoObjectKey(objectKey) {
  return MEMORY_VIDEO_OBJECT_KEY.test(String(objectKey || '').trim());
}

/**
 * 依後端回傳的 mediaType 與 objectKey 推導呈現分類。
 * 明確 VIDEO（或帶影片 metadata）一律為 VIDEO；明確 IMAGE 仍需合法圖片 key；其他已知以外的型別不降級成 IMAGE。
 * 舊資料缺 mediaType 時，只有合法圖片 key 視為 IMAGE，私有影片 key 視為 VIDEO，其餘為 UNSUPPORTED。
 */
export function memoryMediaKind(item) {
  const mediaType = String(item?.mediaType || '').trim().toUpperCase();
  const objectKey = String(item?.objectKey || '').trim();
  if (mediaType === 'VIDEO' || item?.video) return MEMORY_MEDIA_KIND.VIDEO;
  if (mediaType === 'IMAGE') {
    return isMemoryImageObjectKey(objectKey) ? MEMORY_MEDIA_KIND.IMAGE : MEMORY_MEDIA_KIND.UNSUPPORTED;
  }
  if (mediaType) return MEMORY_MEDIA_KIND.UNSUPPORTED;
  if (isMemoryImageObjectKey(objectKey)) return MEMORY_MEDIA_KIND.IMAGE;
  if (isMemoryVideoObjectKey(objectKey)) return MEMORY_MEDIA_KIND.VIDEO;
  return MEMORY_MEDIA_KIND.UNSUPPORTED;
}

const optionalText = (value) => (value == null || value === '' ? null : String(value));
const optionalInteger = (value) => {
  const n = Number(value);
  return Number.isInteger(n) ? n : null;
};

/** 影片 metadata 原樣保留；Long 欄位（byteSize、durationMillis）維持字串，不轉 Number。 */
export function normalizeMemoryVideoMetadata(value) {
  if (!value || typeof value !== 'object') return null;
  return {
    contentType: optionalText(value.contentType),
    byteSize: optionalText(value.byteSize),
    durationMillis: optionalText(value.durationMillis),
    width: optionalInteger(value.width),
    height: optionalInteger(value.height),
    videoCodec: optionalText(value.videoCodec),
    avcProfile: optionalInteger(value.avcProfile),
    audioCodec: optionalText(value.audioCodec),
  };
}

export function normalizeMemoryMediaItem(value) {
  const objectKey = value?.objectKey == null ? '' : String(value.objectKey).trim();
  const displayOrder = Number(value?.displayOrder);
  const mediaType = value?.mediaType == null ? '' : String(value.mediaType).trim().toUpperCase();
  const video = normalizeMemoryVideoMetadata(value?.video);
  const kind = memoryMediaKind({ mediaType, objectKey, video });
  return {
    mediaId: snowflakeId(value?.mediaId),
    objectKey,
    displayOrder: Number.isFinite(displayOrder) ? displayOrder : 0,
    mediaType,
    video,
    kind,
    url: kind === MEMORY_MEDIA_KIND.IMAGE ? resolveMemberMediaUrl(objectKey) : '',
  };
}

/**
 * 編輯中媒體格位的呈現分類：既有媒體沿用 normalizer 的分類；
 * 尚未上傳的新格位只來自已通過圖片檢查的本機檔案；上傳完成後依 objectKey 規則判斷。
 */
export function memoryMediaSlotKind(slot) {
  if (!slot) return MEMORY_MEDIA_KIND.UNSUPPORTED;
  if (slot.kind) return slot.kind;
  if (slot.mediaType || slot.video) return memoryMediaKind(slot);
  if (!slot.objectKey) return MEMORY_MEDIA_KIND.IMAGE;
  return memoryMediaKind(slot);
}

/** 只保留可顯示的圖片媒體（例如分享封面），與後端聊天卡／貼文封面只取 IMAGE 一致。 */
export function memoryWithImageMediaOnly(entry) {
  if (!entry) return entry;
  const media = (Array.isArray(entry.media) ? entry.media : [])
    .filter((item) => item?.kind === MEMORY_MEDIA_KIND.IMAGE);
  return { ...entry, media, hasMedia: media.length > 0 };
}

export function normalizeMemoryMediaList(value) {
  const list = Array.isArray(value) ? value.map(normalizeMemoryMediaItem) : [];
  return list
    .filter((item) => item.objectKey)
    .sort((a, b) => a.displayOrder - b.displayOrder || String(a.mediaId || '').localeCompare(String(b.mediaId || '')));
}

export function formatDaySnapshotLabel(entry) {
  if (!entry?.journeyDayId && !entry?.journeyDayDateSnapshot && !entry?.journeyDayTitleSnapshot) {
    return '';
  }
  const order = entry.journeyDayDisplayOrderSnapshot;
  const dayPart = order == null ? 'Day' : `Day ${Number(order) + 1}`;
  const datePart = entry.journeyDayDateSnapshot || '';
  const titlePart = entry.journeyDayTitleSnapshot || '';
  return [dayPart, datePart, titlePart].filter(Boolean).join(' · ');
}

export function formatActivitySnapshotLabel(entry) {
  const title = entry?.activityTitleSnapshot;
  if (!title) {
    return entry?.activityId ? '原活動（標題未知）' : '';
  }
  return `原活動：${title}`;
}

export function normalizeMemoryEntry(value) {
  const status = String(value?.status || '');
  const visibility = String(value?.visibility || '');
  const memoryType = String(value?.memoryType || '');
  const media = normalizeMemoryMediaList(value?.media);
  const journeyDayId = snowflakeId(value?.journeyDayId);
  const activityId = snowflakeId(value?.activityId);
  const journeyDayDateSnapshot = coerceLocalDate(value?.journeyDayDateSnapshot);
  const journeyDayTitleSnapshot = value?.journeyDayTitleSnapshot == null
    ? ''
    : String(value.journeyDayTitleSnapshot);
  const journeyDayDisplayOrderSnapshot = value?.journeyDayDisplayOrderSnapshot == null
    ? null
    : Number(value.journeyDayDisplayOrderSnapshot);
  const activityTitleSnapshot = value?.activityTitleSnapshot == null
    ? ''
    : String(value.activityTitleSnapshot);
  return {
    memoryEntryId: snowflakeId(value?.memoryEntryId),
    journeyId: snowflakeId(value?.journeyId),
    authorMemberId: snowflakeId(value?.authorMemberId),
    memoryType,
    memoryTypeLabel: memoryTypeLabel(memoryType),
    title: value?.title == null ? '' : String(value.title),
    content: value?.content == null ? '' : String(value.content),
    contentPreview: contentPreview(value?.content),
    visibility,
    visibilityLabel: memoryVisibilityLabel(visibility),
    visibilityHint: memoryVisibilityHint(visibility),
    status,
    statusLabel: memoryStatusLabel(status),
    occurredAt: coerceDateTime(value?.occurredAt),
    occurredAtLocal: coerceDateTimeLocal(value?.occurredAt),
    journeyDayId,
    journeyDayDateSnapshot,
    journeyDayTitleSnapshot,
    journeyDayDisplayOrderSnapshot: Number.isFinite(journeyDayDisplayOrderSnapshot)
      ? journeyDayDisplayOrderSnapshot
      : null,
    activityId,
    activityTitleSnapshot,
    daySnapshotLabel: formatDaySnapshotLabel({
      journeyDayId,
      journeyDayDateSnapshot,
      journeyDayTitleSnapshot,
      journeyDayDisplayOrderSnapshot,
    }),
    activitySnapshotLabel: formatActivitySnapshotLabel({
      activityId,
      activityTitleSnapshot,
    }),
    media,
    hasMedia: media.length > 0,
    rowVersion: value?.rowVersion == null ? null : String(value.rowVersion),
    createTime: coerceDateTime(value?.createTime),
    updateTime: coerceDateTime(value?.updateTime),
    replayed: value?.replayed == null ? null : Boolean(value.replayed),
    isPrivate: visibility === 'PRIVATE',
    isPublic: visibility === 'PUBLIC',
    isActive: status === 'ACTIVE',
    isHidden: status === 'HIDDEN',
    isDeleted: status === 'DELETED',
  };
}

export function normalizeMemoryPage(payload) {
  const items = Array.isArray(payload?.items)
    ? payload.items.map(normalizeMemoryEntry)
    : (Array.isArray(payload) ? payload.map(normalizeMemoryEntry) : []);
  return {
    items,
    nextCursorOccurredAt: payload?.nextCursorOccurredAt == null
      ? null
      : coerceDateTime(payload.nextCursorOccurredAt),
    nextCursorMemoryEntryId: payload?.nextCursorMemoryEntryId == null
      ? null
      : snowflakeId(payload.nextCursorMemoryEntryId),
  };
}

export function emptyMemoryDraft({ memoryType = 'JOURNAL', visibility = 'PRIVATE' } = {}) {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const local = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
  return {
    memoryType,
    title: '',
    content: '',
    visibility: memoryType === 'PRIVATE_NOTE' ? 'PRIVATE' : visibility,
    occurredAt: local,
    journeyDayId: '',
    activityId: '',
  };
}

export function draftFromMemory(entry) {
  return {
    memoryType: entry?.memoryType || 'JOURNAL',
    title: entry?.title || '',
    content: entry?.content || '',
    visibility: entry?.visibility || 'PRIVATE',
    occurredAt: entry?.occurredAtLocal || coerceDateTimeLocal(entry?.occurredAt),
    journeyDayId: entry?.journeyDayId ? String(entry.journeyDayId) : '',
    activityId: entry?.activityId ? String(entry.activityId) : '',
  };
}

export function mediaSlotsFromMemory(entry) {
  return normalizeMemoryMediaList(entry?.media).map((item, index) => ({
    localId: item.mediaId || createLocalMediaId(),
    status: MEDIA_UPLOAD_STATUS.UPLOADED,
    file: null,
    previewUrl: '',
    objectKey: item.objectKey,
    mediaId: item.mediaId,
    // 保留型別與影片 metadata：編輯時影片與無法辨識的媒體只顯示佔位，並隨整組替換原樣送回
    mediaType: item.mediaType,
    video: item.video,
    kind: item.kind,
    displayOrder: index,
    error: '',
  }));
}

export function normalizeMediaDisplayOrder(slots) {
  return (Array.isArray(slots) ? slots : []).map((slot, index) => ({
    ...slot,
    displayOrder: index,
  }));
}

export function moveMediaSlot(slots, localId, direction) {
  const list = normalizeMediaDisplayOrder(slots);
  const index = list.findIndex((slot) => slot.localId === localId);
  if (index < 0) return list;
  const target = direction === 'up' ? index - 1 : index + 1;
  if (target < 0 || target >= list.length) return list;
  const next = list.slice();
  const [row] = next.splice(index, 1);
  next.splice(target, 0, row);
  return normalizeMediaDisplayOrder(next);
}

/**
 * 整組替換的媒體 payload（沿用舊客戶端合約）：
 * 既有 objectKey 不帶 mediaType，後端沿用原型別與 metadata，所以影片與無法辨識的媒體原樣保留；
 * 不宣告 mediaTypeAware，若漏送既有影片，後端回 409 而不會默默刪除。
 */
export function buildMediaPayloadFromSlots(slots) {
  return normalizeMediaDisplayOrder(slots)
    .filter((slot) => slot.status === MEDIA_UPLOAD_STATUS.UPLOADED && slot.objectKey)
    .map((slot, index) => ({
      objectKey: String(slot.objectKey).trim(),
      displayOrder: index,
    }));
}

export function hasUploadingMedia(slots) {
  return (slots || []).some((slot) => slot.status === MEDIA_UPLOAD_STATUS.UPLOADING);
}

export function hasFailedMedia(slots) {
  return (slots || []).some((slot) => slot.status === MEDIA_UPLOAD_STATUS.FAILED);
}

export function optionalSnowflakeOrNull(value) {
  const text = value == null ? '' : String(value).trim();
  return text ? text : null;
}

export function buildCreateMemoryPayload(draft, { clientRequestId, mediaSlots } = {}) {
  const memoryType = draft?.memoryType;
  const visibility = memoryType === 'PRIVATE_NOTE' ? 'PRIVATE' : draft?.visibility;
  const media = buildMediaPayloadFromSlots(mediaSlots);
  return {
    memoryType,
    title: draft?.title == null || String(draft.title).trim() === ''
      ? null
      : String(draft.title).trim(),
    content: draft?.content == null || String(draft.content).trim() === ''
      ? null
      : String(draft.content).trim(),
    visibility,
    occurredAt: toApiDateTime(draft?.occurredAt),
    journeyDayId: optionalSnowflakeOrNull(draft?.journeyDayId),
    activityId: optionalSnowflakeOrNull(draft?.activityId),
    media: media.length ? media : null,
    clientRequestId: String(clientRequestId || '').trim(),
  };
}

export function buildUpdateMemoryPayload(draft, { expectedRowVersion, mediaSlots } = {}) {
  const media = buildMediaPayloadFromSlots(mediaSlots);
  return {
    title: draft?.title == null || String(draft.title).trim() === ''
      ? null
      : String(draft.title).trim(),
    content: draft?.content == null || String(draft.content).trim() === ''
      ? null
      : String(draft.content).trim(),
    occurredAt: toApiDateTime(draft?.occurredAt),
    journeyDayId: optionalSnowflakeOrNull(draft?.journeyDayId),
    activityId: optionalSnowflakeOrNull(draft?.activityId),
    media,
    replaceAssociations: true,
    expectedRowVersion: Number(expectedRowVersion),
    confirm: true,
  };
}

export function buildVisibilityPayload({ visibility, expectedRowVersion } = {}) {
  return {
    visibility,
    expectedRowVersion: Number(expectedRowVersion),
    confirm: true,
  };
}

export function buildCasConfirmPayload({ expectedRowVersion } = {}) {
  return {
    expectedRowVersion: Number(expectedRowVersion),
    confirm: true,
  };
}

export function fileExtension(name) {
  const text = String(name || '');
  const at = text.lastIndexOf('.');
  if (at < 0) return '';
  return text.slice(at).toLowerCase();
}

/** Frontend basic checks only — not a security boundary. */
export function validateImageFile(file) {
  if (!file) return '請選擇圖片檔案。';
  const ext = fileExtension(file.name);
  if (!MEMORY_MEDIA_ALLOWED_EXTENSIONS.includes(ext)) {
    return '僅支援 JPG、JPEG、PNG、GIF。';
  }
  const mime = String(file.type || '').toLowerCase();
  if (mime && !MEMORY_MEDIA_ALLOWED_MIME.includes(mime) && mime !== 'image/jpg') {
    return '僅支援圖片檔案。';
  }
  if (Number(file.size) > MEMORY_MEDIA_MAX_BYTES) {
    return '單張圖片請小於 5MB。';
  }
  return '';
}

export function validateMemoryDraft(draft, { mode = 'create', mediaSlots = [] } = {}) {
  if (mode === 'create') {
    if (!MEMORY_TYPES.includes(draft?.memoryType)) return '請選擇日記類型。';
    if (draft?.memoryType === 'PRIVATE_NOTE') {
      if (draft?.visibility && draft.visibility !== 'PRIVATE') {
        return '私人筆記必須保持私人可見。';
      }
    } else if (!MEMORY_VISIBILITIES.includes(draft?.visibility)) {
      return '請選擇可見範圍。';
    }
  }
  const content = String(draft?.content || '').trim();
  if (!content) return '內容為必填。';
  if (content.length > 10000) return '內容過長。';
  const title = String(draft?.title || '').trim();
  if (title.length > 200) return '標題最多 200 字。';
  if (!toApiDateTime(draft?.occurredAt)) return '請填寫發生時間。';
  if (hasUploadingMedia(mediaSlots)) return '圖片仍在上傳中，請稍候再儲存。';
  const uploaded = buildMediaPayloadFromSlots(mediaSlots);
  if (uploaded.length > MEMORY_MEDIA_BACKEND_MAX) {
    return `最多 ${MEMORY_MEDIA_BACKEND_MAX} 張圖片。`;
  }
  if (uploaded.some((item) => /:\/\//.test(item.objectKey) || item.objectKey.startsWith('blob:'))) {
    return '媒體必須使用 objectKey，不可使用完整 URL。';
  }
  return '';
}

export function activitiesForDay(schedule, journeyDayId) {
  const dayId = journeyDayId == null ? '' : String(journeyDayId);
  const days = Array.isArray(schedule?.days) ? schedule.days : [];
  if (!dayId) {
    const fromDays = days.flatMap((day) => day.activities || []);
    const unscheduled = Array.isArray(schedule?.unscheduledActivities)
      ? schedule.unscheduledActivities
      : [];
    return [...fromDays, ...unscheduled];
  }
  const day = days.find((item) => String(item.journeyDayId) === dayId);
  return day?.activities || [];
}

export function findActivityInSchedule(schedule, activityId) {
  if (!activityId) return null;
  const id = String(activityId);
  for (const day of schedule?.days || []) {
    const hit = (day.activities || []).find((item) => String(item.activityId) === id);
    if (hit) return hit;
  }
  return (schedule?.unscheduledActivities || []).find((item) => String(item.activityId) === id) || null;
}

/**
 * Keep Day/Activity consistent for payload.
 * - Changing Day clears incompatible Activity.
 * - Choosing Activity with a Day syncs journeyDayId.
 */
export function applyDaySelection(draft, journeyDayId) {
  const nextDay = journeyDayId == null ? '' : String(journeyDayId);
  return {
    ...draft,
    journeyDayId: nextDay,
    activityId: '',
  };
}

export function applyActivitySelection(draft, activity, { clearDayIfNone = true } = {}) {
  if (!activity) {
    return {
      ...draft,
      activityId: '',
      ...(clearDayIfNone ? {} : {}),
    };
  }
  const activityDayId = activity.journeyDayId ? String(activity.journeyDayId) : '';
  return {
    ...draft,
    activityId: String(activity.activityId),
    journeyDayId: activityDayId || draft.journeyDayId || '',
  };
}

export function dayChangeClearsActivity(draft, nextDayId, schedule) {
  const activity = findActivityInSchedule(schedule, draft?.activityId);
  if (!activity) return { ...draft, journeyDayId: nextDayId || '', activityId: '' };
  const activityDay = activity.journeyDayId ? String(activity.journeyDayId) : '';
  const next = nextDayId == null ? '' : String(nextDayId);
  if (next && activityDay && activityDay !== next) {
    return { ...draft, journeyDayId: next, activityId: '', activityCleared: true };
  }
  return { ...draft, journeyDayId: next, activityCleared: false };
}

export function formatDayOptionLabel(day) {
  if (!day) return '';
  const order = day.displayOrder == null ? null : Number(day.displayOrder);
  const dayPart = order == null ? 'Day' : `Day ${order + 1}`;
  return [dayPart, day.date || '', day.title || ''].filter(Boolean).join(' · ');
}

export function formatActivityOptionLabel(activity) {
  if (!activity) return '';
  const time = activity.plannedStartTime
    ? (activity.plannedEndTime
      ? `${activity.plannedStartTime}–${activity.plannedEndTime}`
      : activity.plannedStartTime)
    : '';
  return [activity.title || 'Untitled activity', time].filter(Boolean).join(' · ');
}

export function revokeMediaPreview(slot) {
  if (slot?.previewUrl && String(slot.previewUrl).startsWith('blob:')
      && typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') {
    try {
      URL.revokeObjectURL(slot.previewUrl);
    } catch {
      /* ignore */
    }
  }
}

export function revokeAllMediaPreviews(slots) {
  (slots || []).forEach(revokeMediaPreview);
}

export { coerceDateTimeLocal, snowflakeId, toApiDateTime };
